/**
 * asnLocationSummarySlice.ts
 * Redux Toolkit slice for the ASN Gap Location Summary tab — owns both the
 * Chart Configuration (grain/column selection) state AND the
 * `POST /api/asn/location-details` request/response lifecycle
 * (perspective, sort, search, pagination, loading/error, and a
 * request-payload-keyed response cache).
 *
 * Grain config mirrors `onePagerSlice.ts`'s pendingGrains / grainSelections
 * / grainSlotCount / manualOverride / isConfigExpanded pattern exactly — the
 * same store-backed "draft state edited in the panel, committed on Apply"
 * model that `KPIOnePagerV2Tab` uses with `ChartConfigurationPanel`.
 *
 * Grain domain: ASN-native `AsnLocationGrainKey` (`region` | `market` |
 * `location` | `locationType` | `year` | `period` | `week` — see `asnOverviewKpiApi.ts`),
 * matching the keys the backend actually groups rows by.
 *
 * `applyVersion` is a monotonically-incrementing counter bumped by
 * `AsnGapDashboard` whenever the shared filter bar's Apply/Reset is clicked
 * while the Location Summary tab is active. `LocationSummary` watches this
 * counter (via a `useEffect` keyed on it) to know when to re-fetch using the
 * dashboard's current filter selections — this is the only piece of
 * cross-component signalling needed, since every other trigger (grain
 * config Apply/Reset, perspective toggle, sort, search, pagination) is
 * already owned entirely within the Location Summary tab itself.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ASNLocationDetailsRequest,
  ASNLocationDetailsResponse,
  AsnLocationGrainKey,
} from '../../screens/NavigatorReports/pages/AsnGap/api/asnOverviewKpiApi';
import {
  DEFAULT_ASN_PAGE_SIZE,
  DEFAULT_ASN_SORT,
  DEFAULT_GRAIN_SELECTIONS,
  MAX_GRAIN_COLUMNS,
} from '../../screens/NavigatorReports/pages/AsnGap/constants/asnOnePagerConstants';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AsnLocationSummaryPerspective = 'shipping' | 'receiving';

export interface AsnLocationSummaryState {
  // Chart Configuration (grain/column selection)
  /** Working copy of grain selections (edited in the config panel, not yet applied) */
  pendingGrains: string[];
  /** Committed grain selections that drive the table rendering + request */
  grainSelections: AsnLocationGrainKey[];
  /** Number of visible dropdown slots in the config panel */
  grainSlotCount: number;
  /** Whether the user has manually applied a grain config (vs. the default) */
  manualOverride: boolean;
  /** Whether the Chart Configuration panel is expanded */
  isConfigExpanded: boolean;

  // Location Summary tab's own view state (independent of the shared filter bar)
  perspective: AsnLocationSummaryPerspective;
  /** Backend-supported sort field — a grain key or a metric key */
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  searchQuery: string;
  /** Zero-based current page */
  pageNumber: number;
  pageSize: number;

  // API response/status
  data: ASNLocationDetailsResponse | null;
  loading: boolean;
  error: string | null;
  /** Set once the first fetch (triggered by this tab's first activation)
   * has been kicked off — lets the tab avoid re-firing that lazy initial
   * request every time the user switches back to it. */
  hasLoadedOnce: boolean;
  /** The exact request payload most recently sent to
   * `POST /api/asn/location-details`. */
  lastRequest: ASNLocationDetailsRequest | null;

  /** Response cache keyed by a stable stringification of the exact request
   * payload that produced it — if the same request is built again, the
   * cached response is reused instead of calling the API again. */
  cache: Record<string, ASNLocationDetailsResponse>;

  /** Bumped by AsnGapDashboard whenever the shared filter bar's Apply/Reset
   * is clicked while this tab is active — see file header for why. */
  applyVersion: number;
}

const LOCATION_DETAILS_GRAIN_ORDER: AsnLocationGrainKey[] = [
  'region',
  'market',
  'location',
  'locationType',
  'year',
  'period',
  'week',
];

const normalizeLocationSummaryGrains = (
  grains: readonly string[],
): AsnLocationGrainKey[] => {
  const selected = new Set(
    grains.filter((grain): grain is AsnLocationGrainKey =>
      LOCATION_DETAILS_GRAIN_ORDER.includes(grain as AsnLocationGrainKey),
    ),
  );

  const merged = new Set<AsnLocationGrainKey>([
    ...LOCATION_DETAILS_GRAIN_ORDER,
    ...Array.from(selected),
  ]);

  return LOCATION_DETAILS_GRAIN_ORDER.filter((grain) => merged.has(grain));
};

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState: AsnLocationSummaryState = {
  pendingGrains: [...normalizeLocationSummaryGrains(DEFAULT_GRAIN_SELECTIONS)],
  grainSelections: [...normalizeLocationSummaryGrains(DEFAULT_GRAIN_SELECTIONS)],
  grainSlotCount: DEFAULT_GRAIN_SELECTIONS.length,
  manualOverride: false,
  isConfigExpanded: false,

  perspective: 'shipping',
  sortBy: DEFAULT_ASN_SORT.col,
  sortDirection: DEFAULT_ASN_SORT.dir,
  searchQuery: '',
  pageNumber: 0,
  pageSize: DEFAULT_ASN_PAGE_SIZE,

  data: null,
  loading: false,
  error: null,
  hasLoadedOnce: false,
  lastRequest: null,
  cache: {},
  applyVersion: 0,
};

// ─── Cache key helper ─────────────────────────────────────────────────────────

/**
 * Deterministic stringification of a request payload — object keys are
 * sorted recursively (arrays are left in their original order, since
 * `grainSelections` order affects column order and therefore is a
 * meaningful part of request identity). Two requests built from the same
 * inputs always produce the same key, regardless of object key insertion
 * order.
 */
const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`).join(',')}}`;
};

export const buildAsnLocationSummaryCacheKey = (request: ASNLocationDetailsRequest): string =>
  stableStringify(request);

// ─── Slice ────────────────────────────────────────────────────────────────────

const asnLocationSummarySlice = createSlice({
  name: 'asnLocationSummary',
  initialState,
  reducers: {
    // ─── Chart Configuration (grain/column selection) ──────────────────
    toggleAsnConfigPanel(state) {
      state.isConfigExpanded = !state.isConfigExpanded;
    },

    setAsnPendingGrain(state, action: PayloadAction<{ index: number; value: string }>) {
      const { index, value } = action.payload;
      // Clear any other slot that already has this value (enforce uniqueness)
      if (value) {
        state.pendingGrains = state.pendingGrains.map((v) => (v === value ? '' : v));
      }
      while (state.pendingGrains.length <= index) {
        state.pendingGrains.push('');
      }
      state.pendingGrains[index] = value;
    },

    addAsnGrainSlot(state) {
      if (state.grainSlotCount < MAX_GRAIN_COLUMNS) {
        state.grainSlotCount++;
        while (state.pendingGrains.length < state.grainSlotCount) {
          state.pendingGrains.push('');
        }
      }
    },

    removeAsnGrainSlot(state, action: PayloadAction<number>) {
      if (state.grainSlotCount <= 1) return;
      const index = action.payload;
      state.pendingGrains.splice(index, 1);
      state.grainSlotCount--;
      while (state.pendingGrains.length < state.grainSlotCount) {
        state.pendingGrains.push('');
      }
    },

    /** Apply pending grains to committed state (user clicked "Apply"). Also
     * resets pageNumber to 0 per the "column-selection changes" trigger. */
    applyAsnGrainConfig(state) {
      const committed = normalizeLocationSummaryGrains(
        state.pendingGrains.filter((v) => v !== ''),
      );
      if (committed.length === 0) return; // Must have at least one
      state.grainSelections = committed;
      state.pendingGrains = [...committed];
      state.grainSlotCount = Math.max(state.grainSlotCount, committed.length);
      state.manualOverride = true;
      state.pageNumber = 0;
    },

    /** Reset grain config back to the default selection. Also resets
     * pageNumber to 0. */
    resetAsnGrainConfig(state) {
      state.grainSelections = [...normalizeLocationSummaryGrains(DEFAULT_GRAIN_SELECTIONS)];
      state.pendingGrains = [...normalizeLocationSummaryGrains(DEFAULT_GRAIN_SELECTIONS)];
      state.grainSlotCount = DEFAULT_GRAIN_SELECTIONS.length;
      state.manualOverride = false;
      state.pageNumber = 0;
    },

    // ─── Perspective / sort / search / pagination ──────────────────────

    /** Switching perspective changes the request payload entirely (it's
     * sent to the backend), so — like sort/search/grain changes —
     * pageNumber resets to 0. */
    setAsnLocationSummaryPerspective(state, action: PayloadAction<AsnLocationSummaryPerspective>) {
      state.perspective = action.payload;
      state.pageNumber = 0;
    },

    setAsnLocationSummarySort(state, action: PayloadAction<{ sortBy: string; sortDirection: 'asc' | 'desc' }>) {
      state.sortBy = action.payload.sortBy;
      state.sortDirection = action.payload.sortDirection;
      state.pageNumber = 0;
    },

    setAsnLocationSummarySearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
      state.pageNumber = 0;
    },

    setAsnLocationSummaryPageNumber(state, action: PayloadAction<number>) {
      state.pageNumber = action.payload;
    },

    setAsnLocationSummaryPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.pageNumber = 0;
    },

    // ─── API response/status ────────────────────────────────────────────

    setAsnLocationSummaryLoading(state) {
      state.loading = true;
      state.error = null;
    },

    setAsnLocationSummaryData(
      state,
      action: PayloadAction<{ response: ASNLocationDetailsResponse; requestKey: string }>,
    ) {
      state.data = action.payload.response;
      state.loading = false;
      state.error = null;
      state.cache[action.payload.requestKey] = action.payload.response;
    },

    setAsnLocationSummaryError(state, action: PayloadAction<string>) {
      // Never fall back to stale/fixture data — the table renders its own
      // empty/error state while data is null.
      state.data = null;
      state.loading = false;
      state.error = action.payload;
    },

    setAsnLocationSummaryHasLoadedOnce(state) {
      state.hasLoadedOnce = true;
    },

    setAsnLocationSummaryLastRequest(state, action: PayloadAction<ASNLocationDetailsRequest>) {
      state.lastRequest = action.payload;
    },

    /** Bumped by AsnGapDashboard's shared filter bar Apply/Reset handlers
     * (only while this tab is active) — LocationSummary watches this to
     * know when to re-fetch with the newly-applied/reset filters. Also
     * resets pageNumber to 0 per spec. */
    bumpAsnLocationSummaryApplyVersion(state) {
      state.applyVersion += 1;
      state.pageNumber = 0;
    },

    /** Reset all ASN Location Summary state (grain config + view state +
     * API data/cache) to initial. */
    resetAsnLocationSummaryState() {
      return { ...initialState, cache: {} };
    },
  },
});

// ─── Exports ──────────────────────────────────────────────────────────────────

export const {
  toggleAsnConfigPanel,
  setAsnPendingGrain,
  addAsnGrainSlot,
  removeAsnGrainSlot,
  applyAsnGrainConfig,
  resetAsnGrainConfig,
  setAsnLocationSummaryPerspective,
  setAsnLocationSummarySort,
  setAsnLocationSummarySearchQuery,
  setAsnLocationSummaryPageNumber,
  setAsnLocationSummaryPageSize,
  setAsnLocationSummaryLoading,
  setAsnLocationSummaryData,
  setAsnLocationSummaryError,
  setAsnLocationSummaryHasLoadedOnce,
  setAsnLocationSummaryLastRequest,
  bumpAsnLocationSummaryApplyVersion,
  resetAsnLocationSummaryState,
} = asnLocationSummarySlice.actions;

export default asnLocationSummarySlice.reducer;

// ─── Selectors ────────────────────────────────────────────────────────────────

type RootStateWithAsnLocationSummary = { asnLocationSummary: AsnLocationSummaryState };

export const selectAsnGrainSelections = (state: RootStateWithAsnLocationSummary) =>
  state.asnLocationSummary.grainSelections;
export const selectAsnPendingGrains = (state: RootStateWithAsnLocationSummary) =>
  state.asnLocationSummary.pendingGrains;
export const selectAsnGrainSlotCount = (state: RootStateWithAsnLocationSummary) =>
  state.asnLocationSummary.grainSlotCount;
export const selectAsnManualOverride = (state: RootStateWithAsnLocationSummary) =>
  state.asnLocationSummary.manualOverride;
export const selectAsnIsConfigExpanded = (state: RootStateWithAsnLocationSummary) =>
  state.asnLocationSummary.isConfigExpanded;
export const selectAsnLocationSummaryCacheEntry = (
  state: RootStateWithAsnLocationSummary,
  requestKey: string,
): ASNLocationDetailsResponse | undefined => state.asnLocationSummary.cache[requestKey];
export const selectAsnLocationSummaryPerspective = (state: RootStateWithAsnLocationSummary) =>
  state.asnLocationSummary.perspective;
